import { useState, useMemo } from "react"
import styled from "styled-components"

import useApi from "../../../hooks/useApi"
import { useQuery } from "@tanstack/react-query"
import { qk } from "../../../utils/queryKeys"
import Loader from "../../generic/Loader"
import useTranslation from "../../../hooks/useTranslation"

import { Link } from "react-router-dom"
import ViewAll from "../ViewAll"
import { Button, Separator } from '../../generic/Core'
import { Wrapper } from "../../generic/Layout"
import Connect from "../../select/Connect"
import { getFromLS, saveToLS } from "../../../utils/localStore/localStore"
import useScreenSize from "../../../hooks/useScreenSize"
import { useModalStack } from "../../../context/modalStackContext"
import SelectModal from "../../select/SelectModal"
import { Switcher } from "../../generic/Switcher"
import { WidgetTitle } from "../widgetTypography"

const LS_KEY = 'subListWidget'
const LS_KEY_FILTERS = 'subListWidgetFilters'

const CardContent = styled.div`
    width: 100%;

    a:hover {
        text-decoration: underline;
    }
`

// The title with "View all" at its right, as on the analytics cards
const TitleRow = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.25rem;

    h2, h3 { margin: 0; min-width: 0; }
`

const Value = styled.span`
    font-weight: 600;
    color: ${({ theme, $red }) => $red ? theme.emotive.error.color : theme.font.color};
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: ${({ $template }) => $template};
    width: 100%;
`

const GridCell = styled.div`
    padding: 0.35rem 0.5rem 0.35rem 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
`

const CompactFilterText = styled.button`
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    font-size: inherit;
    color: ${({ theme }) => theme.font.color};
    text-decoration: underline;
    text-underline-offset: 0.2em;
    text-align: left;

    &:hover {
        color: ${({ theme }) => theme.primary?.active || theme.font.active};
    }
`


const SubListWidget = ({ type, data, labels }) => {
    const { getMe } = useApi()
    const { t } = useTranslation()
    const { isMobile } = useScreenSize()
    const { pushModal } = useModalStack()

    // Support both header array (new) and legacy connect key
    const connectConfig = useMemo(() => {
        if (Array.isArray(data.header)) {
            return data.header.find(h => h.type === 'connect') ?? null
        }
        return data.connect ?? null
    }, [data])

    const filterConfigs = useMemo(() => {
        if (Array.isArray(data.header)) {
            return data.header.filter(h => h.type === 'filter')
        }
        return []
    }, [data])

    const filterListData = useMemo(() =>
        Object.fromEntries(
            filterConfigs.map(f => [
                f.field,
                { list: f.options.map(o => ({ id: String(o.value ?? ''), name: t(o.label), _value: o.value })) }
            ])
        )
    , [filterConfigs, t])

    const { idField, nameField } = connectConfig

    const [selectorValue, setSelectorValue] = useState(
        () => getFromLS(LS_KEY, type) ?? { [idField]: null, [nameField]: null }
    )

    const [filterValues, setFilterValues] = useState(() => {
        const persisted = getFromLS(LS_KEY_FILTERS, type)
        if (persisted && typeof persisted === 'object') return persisted
        return Object.fromEntries(filterConfigs.map(f => [f.field, f.defaultValue ?? null]))
    })

    const selectedId = selectorValue[idField]

    const handleChange = (events) => {
        const newValue = Array.isArray(events)
            ? events.reduce((acc, { target: { name, value } }) => ({ ...acc, [name]: value || null }), {})
            : (events ?? { [idField]: null, [nameField]: null })
        setSelectorValue(newValue)
        saveToLS(LS_KEY, type, newValue)
    }

    const handleFilterChange = (field, value) => {
        const parsed = value === '' ? null : value
        const next = { ...filterValues, [field]: parsed }
        setFilterValues(next)
        saveToLS(LS_KEY_FILTERS, type, next)
    }

    // Build query URL appending serverFilter params when active
    const queryUrl = useMemo(() => {
        if (!selectedId) return null
        const base = `${connectConfig.entity}/${selectedId}/${data.subEntity}`
        const activeFilters = filterConfigs
            .filter(f => filterValues[f.field] != null && filterValues[f.field] !== '')
            .map(f => `${f.field}:${filterValues[f.field]}`)
        if (activeFilters.length === 0) return base
        return `${base}?serverFilter=${activeFilters.join(';')}`
    }, [selectedId, connectConfig, data.subEntity, filterConfigs, filterValues])

    const subQuery = useQuery({
        queryKey: qk.subList(data.subEntity, selectedId, filterValues),
        queryFn: async () => {
            const response = await getMe(queryUrl)
            if (Array.isArray(response?.data)) return response.data
            throw new Error(`Failed to load ${queryUrl}`)
        },
        enabled: !!selectedId
    })

    return (
        <CardContent>
            <TitleRow>
                <WidgetTitle as="h2">{t(labels.title)}</WidgetTitle>
                {selectedId && <ViewAll to={`${data.path}/${selectedId}`} />}
            </TitleRow>
            <HeaderRow>
                <Connect
                    id={connectConfig.idField}
                    entity={connectConfig.entity}
                    mainValueId={selectorValue?.[connectConfig.idField] ?? null}
                    valueName={selectorValue?.[connectConfig.nameField] ?? null}
                    setValue={handleChange}
                    valueMap={connectConfig.valueMap}
                    placeholder={connectConfig.placeholder}
                    modalTitle={connectConfig.modalTitle}
                    menuItemField={connectConfig.menuItemField}
                    options={connectConfig.options}
                    entityParams={connectConfig.entityParams}
                    $marginBottom={'0rem'}
                    compactOnMobile={connectConfig.compactOnMobile}
                />
                {filterConfigs.map(f => {
                    if (f.compactOnMobile && isMobile) {
                        const activeLabel = f.options.find(o => o.value === filterValues[f.field])?.label
                        const displayLabel = activeLabel ? t(activeLabel) : t(f.options[0]?.label)
                        const openFilterModal = () => pushModal(SelectModal, {
                            isVisible: true,
                            setIsVisible: () => {},
                            data: filterListData[f.field],
                            menuItemField: 'name',
                            modalTitle: f.modalTitle || 'filter',
                            modalOpacity: 0.5,
                            options: {},
                            testId: `${f.field}.filter`,
                            onSelect: (item) => handleFilterChange(f.field, item._value),
                        })
                        return (
                            <CompactFilterText key={f.field} onClick={openFilterModal} data-testid={`${f.field}.filter.compact`}>
                                {displayLabel}
                            </CompactFilterText>
                        )
                    }
                    const switcherOptions = f.options.map(o => ({ id: o.value, name: t(o.label), type: 'item' }))
                    const activeOpt = f.options.find(o => o.value === filterValues[f.field])
                    const currentLabel = activeOpt ? t(activeOpt.label) : t(f.options[0]?.label)
                    return (
                        <Switcher
                            key={f.field}
                            type={f.field}
                            currentOption={currentLabel}
                            options={switcherOptions}
                            handleSelect={opt => handleFilterChange(f.field, opt.id)}
                        />
                    )
                })}
            </HeaderRow>
            <Separator />
            {selectedId && (
                subQuery.isLoading ? <Loader /> :
                subQuery.isError ? <span>Error: Couldn't load!</span> :
                <>
                    {(() => {
                        const lines = Array.isArray(data.lines)
                            ? data.lines
                            : [
                                { field: data.subField, type: 'link', size: 1 },
                                ...(data.subValue ? [{ field: data.subValue, type: 'value', size: 1 }] : [])
                              ]
                        const template = lines.map(l => `${l.size ?? 1}fr`).join(' ')
                        return (
                            <Grid $template={template}>
                                {subQuery.data?.map(e => {
                                    const itemPath = `${data.path}/${selectedId}/${data.subEntity}/${e.id}`
                                    return lines.map((line, i) => (
                                        <GridCell key={`${e.id}-${i}`}>
                                            {line.type === 'link'
                                                ? <Link to={itemPath}>{e[line.field]}</Link>
                                                : <Value>{e[line.field]}</Value>
                                            }
                                        </GridCell>
                                    ))
                                })}
                            </Grid>
                        )
                    })()}

                    {subQuery.data?.length === 0 && (
                        <>
                            <p>{t(labels.emptyDescription)}</p>
                            <Button $shadow={true} $variant='primary'>
                                <Link to={`${data.path}/${selectedId}/${data.subEntity}/new`}>
                                    {t(labels.emptyAction)}
                                </Link>
                            </Button>
                        </>
                    )}
                </>
            )}
        </CardContent>
    )
}

export default SubListWidget
